'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';

import { buildLocalePath, type Locale } from '@/i18n/config';
import { saveStripeCheckoutPayload } from '@/tenants/_shared/lib/stripeCheckoutHandoff';

import { trackRego } from '../../lib/registration-analytics';
import type { RegistrationEntrantSummary } from '../types';

interface UseEmbeddedCheckoutInput {
  eventId: string;
  entrant: RegistrationEntrantSummary | null;
  /** Total charged on the checkout page (order + opted-in platform fee) */
  payableTotal: number;
  /** Donation portion only — analytics dimension */
  donationAmount: number;
  platformFee: number | undefined;
  currencyCode: string;
  currencySymbol: string;
  /** itemId/quantity feed the API; name/price are display-only on the checkout page */
  items: Array<{ itemId: string; quantity: number; name: string; price: number }>;
  donorAddress:
    | {
        streetAddress: string;
        addressLine2?: string;
        city: string;
        state?: string;
        postalCode: string;
        country?: string;
      }
    | undefined;
  donorCountryCode: string | undefined;
  /** Shown as "Donation to …" on the checkout page order summary */
  recipientName: string;
  newsletterOptIn: boolean;
  marketingOptIns: Record<string, boolean>;
  /** Persist the cart before navigating so a cancel-return restores it exactly */
  flushCheckoutState: () => Promise<void>;
  onError: (message: string | null) => void;
}

/**
 * Submit seam for the embedded (Payment Element) payment step: persists the
 * cart, saves the handoff payload to sessionStorage, then routes to the
 * /donate/checkout page which creates the PaymentIntent and takes the payment
 * ("register step 4 collects, page pays"). `isRedirecting` stays true after a
 * successful submit — the page is navigating away.
 */
export function useEmbeddedCheckout(input: UseEmbeddedCheckoutInput) {
  const t = useTranslations('register');
  const router = useRouter();
  const locale = useLocale();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    eventId,
    entrant,
    payableTotal,
    donationAmount,
    platformFee,
    currencyCode,
    currencySymbol,
    items,
    donorAddress,
    donorCountryCode,
    recipientName,
    newsletterOptIn,
    marketingOptIns,
    flushCheckoutState,
    onError,
  } = input;

  const startCheckout = useCallback(async (): Promise<boolean> => {
    if (isRedirecting) return false;
    // A null entrant here means the resume path failed to hydrate it. Surface a
    // visible error instead of silently returning, so this can never again be an
    // invisible "dead Pay button".
    if (!entrant) {
      trackRego('rego_error', {
        step: 'checkout',
        error_type: 'missing_entrant_on_submit',
        error_message: 'entrant null at checkout',
      });
      onError(t('registrationFailed'));
      return false;
    }

    onError(null);
    setIsRedirecting(true);
    try {
      await flushCheckoutState();

      const key = saveStripeCheckoutPayload({
        scope: { scope: 'registration', eventId },
        amount: payableTotal,
        baseAmount: payableTotal - (platformFee ?? 0),
        platformFee: platformFee ?? 0,
        currency: { code: currencyCode, symbol: currencySymbol },
        donor: {
          donorEmail: entrant.email,
          donorFirstName: entrant.firstName,
          donorLastName: entrant.lastName,
          isAnonymous: false,
          giftAidEligible: false,
        },
        donorCountryCode: donorCountryCode ?? '',
        donorAddress,
        recipientName,
        returnTo: window.location.pathname,
        items: items.length > 0 ? items : undefined,
        registrationCompletion: {
          eventId,
          termsAccepted: true,
          newsletterOptIn,
          marketingOptIns,
        },
      });

      if (!key) {
        // sessionStorage unavailable — there is no payload to hand off.
        trackRego('rego_error', {
          step: 'checkout',
          error_type: 'embedded_handoff_failed',
          error_message: 'sessionStorage unavailable',
        });
        onError(t('registrationFailed'));
        setIsRedirecting(false);
        return false;
      }

      trackRego('rego_checkout_initiated', {
        donation_amount: donationAmount,
        has_merch: items.length > 0,
        currency: currencyCode,
        checkout_mode: 'embedded',
      });

      router.push(buildLocalePath(locale as Locale, `/donate/checkout?k=${key}`));
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('registrationFailed');
      trackRego('rego_error', {
        step: 'checkout',
        error_type: 'embedded_handoff_failed',
        error_message: message,
      });
      onError(message);
      setIsRedirecting(false);
      return false;
    }
  }, [
    entrant,
    isRedirecting,
    onError,
    flushCheckoutState,
    eventId,
    payableTotal,
    donationAmount,
    platformFee,
    currencyCode,
    currencySymbol,
    items,
    donorAddress,
    donorCountryCode,
    recipientName,
    newsletterOptIn,
    marketingOptIns,
    router,
    locale,
    t,
  ]);

  return { startCheckout, isRedirecting };
}
